import {
  PLAYBACK_SPEEDS,
  GOVERNOR_TICK_MS,
  GOVERNOR_MAX_TICK_SECONDS,
  PREROLL_CUSHION_WALL_SECONDS,
  REBUFFER_HOLD_WALL_SECONDS,
  HARD_SEEK_DRIFT_SECONDS,
  DEAD_TILE_TIMEOUT_SECONDS,
  REGISTRATION_SETTLE_MS,
  CLOCK_MIRROR_INTERVAL_MS,
  CLOCK_MIRROR_THRESHOLD_SECONDS,
  BUFFERED_TO_END_TOLERANCE_SECONDS,
  PLAYBACK_RATE_EPSILON,
  NORMAL_VIDEO_BUFFER_BUDGET,
} from "components/scrapEyeDataDashboard/VideoPlayer/constants";
import { computeBufferLength } from "hooks/useHlsVideo";
import {
  createVideoPlaybackReport,
  VideoPlaybackReport,
} from "hooks/videoPlaybackReport";
import React, {
  createContext,
  use,
  useReducer,
  useRef,
  useMemo,
  useEffect,
  useCallback,
  useState,
} from "react";

const PLAYBACK_RATE_STORAGE_KEY = "videoPlayerPlaybackRate";

// readyState below this: tile is decode-stalled; the clock must not advance past it.
const HAVE_FUTURE_DATA = 3;

const getInitialPlaybackRate = (): number => {
  const stored = localStorage.getItem(PLAYBACK_RATE_STORAGE_KEY);
  if (stored !== null) {
    const rate = Number(stored);
    if (Number.isFinite(rate) && rate > 0) {
      return rate;
    }
  }
  return PLAYBACK_SPEEDS[PLAYBACK_SPEEDS.length - 1] ?? 1;
};

export interface VideoControlState {
  isPlaying: boolean;
  playbackRate: number;
  currentTime: number;
  duration: number;
  videoFps: number | null;
  isBuffering: boolean;
}

export interface VideoRegistrationMeta {
  videoIndex: number;
  isLive?: boolean;
  getForwardBufferEnd?: () => number | null;
  onSeek?: (target: number) => void;
}

export interface VideoControlActions {
  play: () => void;
  pause: () => void;
  togglePlayPause: () => void;
  setPlaybackRate: (rate: number) => void;
  seekTo: (time: number, durationCeiling?: number) => void;
  registerVideoRef: (
    id: string,
    ref: HTMLVideoElement,
    meta: VideoRegistrationMeta
  ) => void;
  unregisterVideoRef: (id: string) => void;
  updateDuration: (duration: number) => void;
  setVideoStartOffset: (videoId: string, offset: number) => void;
  reset: () => void;
  getVideoRefs: () => Map<string, HTMLVideoElement>;
  getLiveCurrentTime: () => number;
  takeHoldCauseMs: (id: string) => number;
  playbackReport: VideoPlaybackReport;
}

const VideoControlStateContext = createContext<VideoControlState | null>(null);
const VideoControlActionsContext = createContext<VideoControlActions | null>(
  null
);

export const useVideoControlState = (): VideoControlState => {
  const context = use(VideoControlStateContext);
  if (!context) {
    throw new Error(
      "useVideoControlState must be used within VideoControlProvider"
    );
  }
  return context;
};

export const useVideoControlActions = (): VideoControlActions => {
  const context = use(VideoControlActionsContext);
  if (!context) {
    throw new Error(
      "useVideoControlActions must be used within VideoControlProvider"
    );
  }
  return context;
};

export const useVideoControl = (): {
  state: VideoControlState;
  actions: VideoControlActions;
} => {
  const state = useVideoControlState();
  const actions = useVideoControlActions();
  return { state, actions };
};

interface VideoControlProviderProps {
  children: React.ReactNode;
  autoPlay?: boolean;
  videoFps?: number | null;
  playableRange?: Readonly<{ start: number; end: number }>;
}

type Action =
  | { type: "PLAY" }
  | { type: "PAUSE" }
  | { type: "TOGGLE" }
  | { type: "SET_RATE"; rate: number }
  | { type: "SET_CURRENT_TIME"; time: number }
  | { type: "UPDATE_DURATION"; duration: number }
  | { type: "SET_BUFFERING"; isBuffering: boolean }
  | { type: "RESET" };

function reducer(state: VideoControlState, action: Action): VideoControlState {
  switch (action.type) {
    case "PLAY":
      return { ...state, isPlaying: true };
    case "PAUSE":
      return { ...state, isPlaying: false };
    case "TOGGLE":
      return { ...state, isPlaying: !state.isPlaying };
    case "SET_RATE":
      localStorage.setItem(PLAYBACK_RATE_STORAGE_KEY, action.rate.toString());
      return { ...state, playbackRate: action.rate };
    case "SET_CURRENT_TIME":
      if (action.time === state.currentTime) return state;
      return { ...state, currentTime: action.time };
    case "UPDATE_DURATION":
      // Live HLS streams report Infinity/NaN durations; guard so a stray value
      // can't corrupt the shared timeline (Math.max(x, Infinity) is Infinity).
      if (!Number.isFinite(action.duration)) return state;
      return { ...state, duration: Math.max(state.duration, action.duration) };
    case "SET_BUFFERING":
      if (action.isBuffering === state.isBuffering) return state;
      return { ...state, isBuffering: action.isBuffering };
    case "RESET":
      return {
        ...state,
        currentTime: 0,
        duration: 0,
        isBuffering: true,
      };
    default:
      return state;
  }
}

export const VideoControlProvider: React.FC<VideoControlProviderProps> = ({
  children,
  autoPlay = true,
  videoFps = null,
  playableRange,
}) => {
  const [state, dispatch] = useReducer(reducer, {
    isPlaying: autoPlay,
    playbackRate: getInitialPlaybackRate(),
    currentTime: 0,
    duration: 0,
    videoFps,
    isBuffering: true,
  });

  const videoElementsRef = useRef(new Map<string, HTMLVideoElement>());
  const videoMetaRef = useRef(new Map<string, VideoRegistrationMeta>());
  const videoOffsetsRef = useRef(new Map<string, number>());
  const registeredAtRef = useRef(new Map<string, number>());

  const playableRangeRef = useRef(playableRange);
  playableRangeRef.current = playableRange;

  const wallClockRef = useRef(0);
  const lastTickWallRef = useRef(0);
  const lastMirrorWallRef = useRef(0);
  const lastRegistrationWallRef = useRef(0);
  const holdingRef = useRef(true);
  const runningRef = useRef(false);
  const lastSafeTRef = useRef(0);
  const rebufferingRef = useRef(false);
  const holdCauseMsRef = useRef(new Map<string, number>());

  const stateRef = useRef(state);
  stateRef.current = state;

  const clampToPlayable = useCallback(
    (time: number, durationCeiling?: number) => {
      const floor = playableRangeRef.current?.start ?? 0;
      const ceiling = Math.min(
        (durationCeiling ?? stateRef.current.duration) || Infinity,
        playableRangeRef.current?.end ?? Infinity
      );
      return Math.max(floor, Math.min(time, ceiling));
    },
    []
  );

  const seekHistoricalTiles = useCallback(
    (target: number, origin: "user" | "reset") => {
      videoElementsRef.current.forEach((v, id) => {
        if (videoMetaRef.current.get(id)?.isLive) return;
        const offset = videoOffsetsRef.current.get(id) ?? 0;
        try {
          if (origin === "user")
            videoMetaRef.current.get(id)?.onSeek?.(target + offset);
          v.currentTime = target + offset;
        } catch (e) {
          if (!(e instanceof DOMException)) throw e;
        }
      });
    },
    []
  );

  useEffect(() => {
    lastTickWallRef.current = performance.now();
    let visibleSince = performance.now();
    const visibilityChanged = () => {
      if (!document.hidden) visibleSince = performance.now();
    };

    const tick = () => {
      const now = performance.now();
      const previousTickWall = lastTickWallRef.current;
      const dtRaw = (now - previousTickWall) / 1000;
      lastTickWallRef.current = now;
      const { isPlaying, playbackRate, duration } = stateRef.current;

      const participating: {
        id: string;
        v: HTMLVideoElement;
        offset: number;
        end: number;
      }[] = [];
      let waitingForPending = false;
      let historicalCount = 0;

      for (const [id, v] of videoElementsRef.current) {
        const meta = videoMetaRef.current.get(id);
        if (!meta || meta.isLive) continue;
        historicalCount += 1;
        const end = meta.getForwardBufferEnd?.() ?? null;
        if (end !== null) {
          const offset = videoOffsetsRef.current.get(id) ?? 0;
          participating.push({ id, v, offset, end });
          continue;
        }
        const age = now - (registeredAtRef.current.get(id) ?? now);
        if (age < DEAD_TILE_TIMEOUT_SECONDS * 1000) waitingForPending = true;
      }

      if (historicalCount === 0) {
        runningRef.current = false;
        if (stateRef.current.isBuffering) {
          dispatch({ type: "SET_BUFFERING", isBuffering: false });
        }
        return;
      }

      if (participating.length === 0) {
        holdingRef.current = true;
        rebufferingRef.current = false;
        runningRef.current = false;
        const wantBuffering = stateRef.current.isPlaying;
        if (wantBuffering !== stateRef.current.isBuffering) {
          dispatch({ type: "SET_BUFFERING", isBuffering: wantBuffering });
        }
        videoElementsRef.current.forEach((v, id) => {
          if (!videoMetaRef.current.get(id)?.isLive && !v.paused) v.pause();
        });
        return;
      }

      const cushionMedia = Math.min(
        PREROLL_CUSHION_WALL_SECONDS * playbackRate,
        computeBufferLength(false, playbackRate, NORMAL_VIDEO_BUFFER_BUDGET)
      );
      const holdFloorMedia = Math.min(
        REBUFFER_HOLD_WALL_SECONDS * playbackRate,
        cushionMedia
      );
      const settled =
        now - lastRegistrationWallRef.current >= REGISTRATION_SETTLE_MS;

      let minForward = Infinity;
      let minDownloadingForward = Infinity;
      let heldForIds: string[] = [];
      let safeT = Infinity;
      let allBufferedToEnd = true;
      for (const { id, v, offset, end } of participating) {
        if (v.ended) continue;
        const tileT = v.currentTime - offset;
        const decodeStalled = v.readyState < HAVE_FUTURE_DATA;
        const capT = decodeStalled ? tileT : end - offset;
        if (capT < safeT) safeT = capT;
        const forward = decodeStalled ? 0 : end - v.currentTime;
        if (forward < minForward) minForward = forward;
        const bufferedToEnd =
          Number.isFinite(v.duration) &&
          end >= v.duration - BUFFERED_TO_END_TOLERANCE_SECONDS;
        if (!bufferedToEnd) allBufferedToEnd = false;
        if (bufferedToEnd && !decodeStalled) continue;
        if (forward < minDownloadingForward) {
          minDownloadingForward = forward;
          heldForIds = [id];
        } else if (forward === minDownloadingForward) {
          heldForIds.push(id);
        }
      }
      lastSafeTRef.current = safeT;

      const allEnded = participating.every(({ v }) => v.ended);
      if (allEnded) {
        holdingRef.current = false;
        rebufferingRef.current = false;
        if (stateRef.current.isPlaying) dispatch({ type: "PAUSE" });
      } else if (holdingRef.current) {
        // A clip shorter than cushionMedia (cushionWall * rate) can never buffer
        // cushionMedia ahead; release once every tile is buffered to its own end.
        if (
          settled &&
          !waitingForPending &&
          (minForward >= cushionMedia || allBufferedToEnd)
        ) {
          holdingRef.current = false;
          rebufferingRef.current = false;
        }
      } else if (
        waitingForPending ||
        (minForward < holdFloorMedia && !allBufferedToEnd)
      ) {
        holdingRef.current = true;
        rebufferingRef.current = true;
      }

      const running = isPlaying && !holdingRef.current && !allEnded;
      runningRef.current = running;

      if (
        isPlaying &&
        holdingRef.current &&
        rebufferingRef.current &&
        !waitingForPending &&
        !document.hidden &&
        heldForIds.length > 0
      ) {
        const causes = holdCauseMsRef.current;
        const heldMs =
          (now - Math.max(previousTickWall, visibleSince)) / heldForIds.length;
        for (const id of heldForIds) {
          causes.set(id, (causes.get(id) ?? 0) + heldMs);
        }
      }

      if (running) {
        const dt = Math.min(Math.max(dtRaw, 0), GOVERNOR_MAX_TICK_SECONDS);
        const proposed = wallClockRef.current + dt * playbackRate;
        const ceiling = Math.min(
          duration || Infinity,
          playableRangeRef.current?.end ?? Infinity
        );
        const advanced = Math.min(proposed, safeT, ceiling);
        if (advanced > wallClockRef.current) wallClockRef.current = advanced;
        if (
          playableRangeRef.current !== undefined &&
          wallClockRef.current >= playableRangeRef.current.end
        ) {
          dispatch({ type: "PAUSE" });
        }
      }

      const clock = wallClockRef.current;
      for (const { v, offset } of participating) {
        const target = clock + offset;
        if (Math.abs(v.playbackRate - playbackRate) > PLAYBACK_RATE_EPSILON) {
          v.playbackRate = playbackRate;
        }
        if (running) {
          // A decode-stalled tile is left alone — it recovers while the clock
          // holds for it; seeking it forward would only skip footage.
          if (v.readyState >= HAVE_FUTURE_DATA) {
            if (
              !v.ended &&
              Math.abs(v.currentTime - target) > HARD_SEEK_DRIFT_SECONDS
            ) {
              v.currentTime = target;
            }
            if (v.paused && !v.ended) v.play().catch(console.warn);
          }
        } else {
          if (!v.paused) v.pause();
          if (
            !v.ended &&
            Math.abs(v.currentTime - target) > HARD_SEEK_DRIFT_SECONDS
          ) {
            v.currentTime = target;
          }
        }
      }

      if (now - lastMirrorWallRef.current >= CLOCK_MIRROR_INTERVAL_MS) {
        lastMirrorWallRef.current = now;
        if (
          Math.abs(clock - stateRef.current.currentTime) >
          CLOCK_MIRROR_THRESHOLD_SECONDS
        ) {
          dispatch({ type: "SET_CURRENT_TIME", time: clock });
        }
        const wantBuffering = holdingRef.current && stateRef.current.isPlaying;
        if (wantBuffering !== stateRef.current.isBuffering) {
          dispatch({ type: "SET_BUFFERING", isBuffering: wantBuffering });
        }
      }
    };

    document.addEventListener("visibilitychange", visibilityChanged);
    const interval = setInterval(tick, GOVERNOR_TICK_MS);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", visibilityChanged);
    };
  }, []);

  const play = useCallback(() => {
    dispatch({ type: "PLAY" });
  }, []);
  const pause = useCallback(() => {
    dispatch({ type: "PAUSE" });
  }, []);
  const togglePlayPause = useCallback(() => {
    dispatch({ type: "TOGGLE" });
  }, []);
  const setPlaybackRate = useCallback((rate: number) => {
    dispatch({ type: "SET_RATE", rate });
  }, []);

  const seekTo = useCallback(
    // durationCeiling lets a caller seek against a duration it just
    // dispatched in the same commit, before stateRef.current.duration
    // reflects that dispatch on the next render.
    (time: number, durationCeiling?: number) => {
      const clamped = clampToPlayable(time, durationCeiling);
      wallClockRef.current = clamped;
      lastSafeTRef.current = clamped;
      holdingRef.current = true;
      rebufferingRef.current = false;
      seekHistoricalTiles(clamped, "user");
      dispatch({ type: "SET_CURRENT_TIME", time: clamped });
      dispatch({ type: "SET_BUFFERING", isBuffering: true });
    },
    [clampToPlayable, seekHistoricalTiles]
  );

  const updateDuration = useCallback((duration: number) => {
    dispatch({ type: "UPDATE_DURATION", duration });
  }, []);

  const setVideoStartOffset = useCallback((videoId: string, offset: number) => {
    videoOffsetsRef.current.set(videoId, offset);
  }, []);

  const reset = useCallback(() => {
    const start = clampToPlayable(0);
    wallClockRef.current = start;
    lastSafeTRef.current = start;
    holdingRef.current = true;
    rebufferingRef.current = false;
    const now = performance.now();
    lastRegistrationWallRef.current = now;
    videoElementsRef.current.forEach((_v, id) => {
      registeredAtRef.current.set(id, now);
    });
    videoOffsetsRef.current.clear();
    dispatch({ type: "RESET" });
    dispatch({ type: "SET_CURRENT_TIME", time: start });
    seekHistoricalTiles(start, "reset");
  }, [clampToPlayable, seekHistoricalTiles]);

  const registerVideoRef = useCallback(
    (id: string, ref: HTMLVideoElement, meta: VideoRegistrationMeta) => {
      videoElementsRef.current.set(id, ref);
      videoMetaRef.current.set(id, meta);
      registeredAtRef.current.set(id, performance.now());
      lastRegistrationWallRef.current = performance.now();
      holdingRef.current = true;
      rebufferingRef.current = false;
      if (!meta.isLive) {
        ref.playbackRate = stateRef.current.playbackRate;
        const offset = videoOffsetsRef.current.get(id) ?? 0;
        try {
          ref.currentTime = wallClockRef.current + offset;
        } catch (e) {
          if (!(e instanceof DOMException)) throw e;
        }
      }
    },
    []
  );

  const unregisterVideoRef = useCallback((id: string) => {
    videoElementsRef.current.delete(id);
    videoMetaRef.current.delete(id);
    videoOffsetsRef.current.delete(id);
    registeredAtRef.current.delete(id);
    holdCauseMsRef.current.delete(id);
    lastRegistrationWallRef.current = performance.now();
  }, []);

  const takeHoldCauseMs = useCallback((id: string) => {
    const ms = holdCauseMsRef.current.get(id) ?? 0;
    holdCauseMsRef.current.delete(id);
    return ms;
  }, []);

  const getVideoRefs = useCallback(() => {
    return videoElementsRef.current;
  }, []);

  const [playbackReport] = useState(createVideoPlaybackReport);
  useEffect(() => {
    playbackReport.start();
    return playbackReport.close;
  }, [playbackReport]);

  // Extrapolated to the calling instant (clamped to lastSafeT) so the rAF cursor
  // reads a smooth value between governor ticks instead of stepping.
  const getLiveCurrentTime = useCallback((): number => {
    let hasHistorical = false;
    for (const meta of videoMetaRef.current.values()) {
      if (!meta.isLive) {
        hasHistorical = true;
        break;
      }
    }
    if (!hasHistorical) {
      return clampToPlayable(stateRef.current.currentTime);
    }
    if (!runningRef.current) {
      return clampToPlayable(wallClockRef.current);
    }
    const elapsed = Math.min(
      Math.max((performance.now() - lastTickWallRef.current) / 1000, 0),
      GOVERNOR_MAX_TICK_SECONDS
    );
    const extrapolated =
      wallClockRef.current + elapsed * stateRef.current.playbackRate;
    return clampToPlayable(Math.min(extrapolated, lastSafeTRef.current));
  }, [clampToPlayable]);

  const actions: VideoControlActions = useMemo(
    () => ({
      play,
      pause,
      togglePlayPause,
      setPlaybackRate,
      seekTo,
      registerVideoRef,
      unregisterVideoRef,
      updateDuration,
      setVideoStartOffset,
      reset,
      getVideoRefs,
      getLiveCurrentTime,
      takeHoldCauseMs,
      playbackReport,
    }),
    [
      play,
      pause,
      togglePlayPause,
      setPlaybackRate,
      seekTo,
      registerVideoRef,
      unregisterVideoRef,
      updateDuration,
      setVideoStartOffset,
      reset,
      getVideoRefs,
      getLiveCurrentTime,
      takeHoldCauseMs,
      playbackReport,
    ]
  );

  return (
    <VideoControlActionsContext value={actions}>
      <VideoControlStateContext value={state}>
        {children}
      </VideoControlStateContext>
    </VideoControlActionsContext>
  );
};
